import { last } from 'lodash';
import { useCallback, useMemo, useRef } from 'react';
import {
  findElement,
  flattenElements,
  getAllBuildingVersions,
  getChildElements,
  getElementChildrenDepth,
  getProductElementPathsRecord,
  isDeletedElement,
} from '@shared/helpers/recursive-element.helpers';
import {
  Baseline,
  IElement,
  OneOfChildElements,
  OneOfElements,
  OneOfListElements,
  OneOfParentElements,
} from '@shared/models/project.model';
import {
  getSelectedVersion,
  useSelectedElement,
  useSelectedVersion,
} from '@client/store/ui';
import { ArrayOrSingle, ItemOrItemId } from '@shared/models/type-helpers.model';
import { PathRecord } from '@shared/helpers/element-category.helpers';
import { useFlatSortFilteredElements } from './use-filter-elements.hooks';
import { getPathInFlatTree } from '@shared/helpers/tree.helpers';
import { isProjectInfoOrFolder } from '@shared/helpers/project-folder.helpers';
import { isDeactivated } from '@shared/helpers/element.helpers';
import { useProjectStateStore } from '@client/store/project/project.store';

/**
 * Get the path, all parents excluding itself, to an element.
 * @param element
 * @returns
 */
export const useElementPath = (
  element?: OneOfListElements,
): OneOfParentElements[] => {
  const allElements = useFlatSortFilteredElements();

  // Search in all versions, starting with the selected version
  return useMemo(() => {
    if (isProjectInfoOrFolder(element) || !element) {
      return [];
    }
    return [
      getSelectedVersion(true),
      ...(getPathInFlatTree(
        allElements,
        element,
        'elements',
      ) as OneOfParentElements[]),
    ];
  }, [allElements, element]);
};

/**
 * Get parent element of an element.
 * @param element
 * @returns
 */
export const useParentElement = (
  element: OneOfListElements,
): OneOfParentElements | undefined => last(useElementPath(element));

/**
 * Get all versions.
 * @returns
 */
export const useVersions = (): Baseline[] => {
  return useProjectStateStore(({ project }) => getAllBuildingVersions(project));
};

/**
 * Get all versions, with the selectedVersion first.
 * This to increase performance since most operations are done on the selected version.
 * @returns
 */
export const useVersionsWithSelectedFirst = (): Baseline[] => {
  const versions = useVersions();
  const selected = useSelectedVersion();
  return useMemo(
    () =>
      selected
        ? [selected, ...versions.filter((v) => v !== selected)]
        : versions,
    [versions, selected],
  );
};

export const useElementById = (
  elementOrId: ItemOrItemId<OneOfElements> | undefined,
): OneOfElements | undefined => {
  const version = useSelectedVersion();
  return useMemo(() => {
    if (typeof elementOrId === 'string') {
      return findElement(version, elementOrId);
    }
    return elementOrId;
  }, [elementOrId, version]);
};

/**
 * Only to use with TEMP_ELEMENTS
 * @returns
 */
export const useFirstDeletedElement = () => {
  const version = useSelectedVersion();
  return useMemo(() => {
    return findElement(version, isDeletedElement) as IElement | undefined;
  }, [version]);
};

export const useElementChildren = <T extends OneOfListElements>(
  element: T | undefined,
): OneOfChildElements<T>[] => {
  // To not create a new array for empty elements
  const empty = useRef<OneOfChildElements<T>[]>([]);
  const children = getChildElements(element);
  return children.length > 0 ? children : empty.current;
};

export const useIsSelectedElement = (element?: OneOfElements): boolean => {
  const selected = useSelectedElement();
  return !!element && selected?.id === element.id;
};

export const useIsRootElement = (): ((element: IElement) => boolean) => {
  const selectedVersion = useSelectedVersion();

  if (!selectedVersion) {
    throw new Error("Couldn't find version");
  }
  return useCallback(
    (element) =>
      selectedVersion.elements.some((child) => child.id === element.id),
    [selectedVersion.elements],
  );
};

/**
 * Get how many levels of elements there are in an element.
 * 0 for empty, 1 for element with only root elements, and so on.
 * @param element
 * @returns
 */
export const useElementChildrenDepth = (element?: OneOfElements): number => {
  return useMemo(() => getElementChildrenDepth(element), [element]);
};

/**
 * Get how many levels of elements there are in the selected version.
 * 0 for empty version, 1 for version with only root elements, and so on.
 * @returns
 */
export const useSelectVersionDepth = (): number => {
  const selectedVersion = useSelectedVersion();
  return useElementChildrenDepth(selectedVersion);
};

/**
 * Get all children in an element recursively, including self.
 * @param element
 * @returns
 */
export const useFlattenedElement = (
  element?: OneOfElements,
): OneOfChildElements[] => {
  return useMemo(() => {
    return flattenElements(element);
  }, [element]);
};

/**
 * Get all children in an element recursively, excluding self.
 * @param element
 * @returns
 */
export const useFlattenedChildren = (
  element?: OneOfElements,
): OneOfChildElements[] => {
  const children = useElementChildren(element);

  return useMemo(() => {
    return flattenElements(...children);
  }, [children]);
};

/**
 * Get all children in the selected version recursively, excluding version.
 * @returns
 */
export const useFlattenedChildrenInSelectedVersion =
  (): OneOfChildElements[] => {
    const version = useSelectedVersion();
    return useFlattenedChildren(version);
  };

export const useProductElementPathsRecord = (
  rootElements: ArrayOrSingle<OneOfElements> | undefined,
  getPathToAllElements = false,
): PathRecord =>
  useMemo(
    () => getProductElementPathsRecord(rootElements, getPathToAllElements),
    [getPathToAllElements, rootElements],
  );

/**
 * Check the element or any of its parents are deactivated.
 * Proposal visibility is baked into {@link IElement.isDeactivated}.
 */
export const useIsDeactivated = (
  element: OneOfElements,
  countInactiveVersionAsDeactivated?: boolean,
): boolean => {
  const path = useElementPath(element);

  const countInactive = countInactiveVersionAsDeactivated ?? false;

  return [...path, element].some((e) => isDeactivated(e, countInactive));
};
